import { Descendant } from 'slate'

import { TypographyGroupValue, TypographySelectValue } from '@makyoapp/makyo-common'
import { ComponentAlignment, getTextColorStyles, RawSpacingInput, TextAlignment } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

import { CompHeadline } from '../../shared/CompHeadline'
import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'
import { CompLinearProgressProps } from './comp'

export interface CompLinearProgressWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  children: (props: CompLinearProgressProps) => React.ReactNode
}

/**
 * CompLinearProgressWrapperCore
 *
 * todo: useTypographyStylePropsV2
 */
export const CompLinearProgressWrapperCore = ({
  infos: { settings, containerClient, detailTableId, detailRowIndex, currentParentBg, component },
  children,
}: CompLinearProgressWrapperCoreProps) => {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const maxValue = useParseVariableSelect(
    settings[ComponentSettingKey.LinearProgressDataMaxValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const currentValue = useParseVariableSelect(
    settings[ComponentSettingKey.LinearProgressDataValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const heading = settings[ComponentSettingKey.LinearProgressDataTitle] ?? []

  // ========================= design =========================

  const width = getSafely(settings, [ComponentSettingKey.LinearProgressDesignWidth], [100])[0]

  const height = getSafely(settings, [ComponentSettingKey.LinearProgressDesignHeight], [4])[0]

  const compAlignment = getSafely(settings, [ComponentSettingKey.LinearProgressDesignAlignment], 'start')

  const headingAlignment = getSafely(settings, [ComponentSettingKey.LinearProgressDesignHeadingAlignment], 'start')

  const typoSetting = settings?.[ComponentSettingKey.LinearProgressDesignTypography] as TypographyGroupValue

  const getHeadingTypo = typoSetting?.[ComponentSettingKey.LinearProgressDesignHeadingTypography]
  const headingTypography = getHeadingTypo || { typography: 'Body Medium' }
  const valueTypography = (typoSetting[
    ComponentSettingKey.LinearProgressDesignValueTypography
  ] as TypographySelectValue) ?? {
    typography: 'Body Large',
  }
  const headingMargin = settings[ComponentSettingKey.LinearProgressDesignTitleMargin] as RawSpacingInput

  const margin = settings[ComponentSettingKey.LinearProgressDesignMargin] as RawSpacingInput

  const valueSpacing = settings[ComponentSettingKey.LinearProgressDesignValueMargin] as RawSpacingInput

  const valueColorType = settings[ComponentSettingKey.LinearProgressDesignValueColor]
  const valueColorSolid = settings[ComponentSettingKey.LinearProgressDesignValueColorSolid] || 'primary-40'
  const valueColorStartGradient =
    settings[ComponentSettingKey.LinearProgressDesignValueColorStartGradient] || 'primary-40'
  const valueColorEndGradient = settings[ComponentSettingKey.LinearProgressDesignValueColorEndGradient] || 'primary-40'
  const valueColorGradientDirection = settings[ComponentSettingKey.LinearProgressDesignValueColorGradientDirection]

  const labelColorType = settings[ComponentSettingKey.LinearProgressDesignLabelColor]
  const labelColorSolid = settings[ComponentSettingKey.LinearProgressDesignLabelColorSolid] || 'primary-40'
  const labelColorStartGradient =
    settings[ComponentSettingKey.LinearProgressDesignLabelColorStartGradient] || 'primary-40'
  const labelColorEndGradient = settings[ComponentSettingKey.LinearProgressDesignLabelColorEndGradient] || 'primary-40'
  const labelColorGradientDirection = settings[ComponentSettingKey.LinearProgressDesignLabelColorGradientDirection]

  const indicatorColorType = settings[ComponentSettingKey.LinearProgressDesignCircleColor]
  const indicatorColorSolid = settings[ComponentSettingKey.LinearProgressDesignCircleColorSolid] || 'primary-40'
  const indicatorColorStartGradient =
    settings[ComponentSettingKey.LinearProgressDesignCircleColorStartGradient] || 'primary-40'
  const indicatorColorEndGradient =
    settings[ComponentSettingKey.LinearProgressDesignCircleColorEndGradient] || 'primary-40'
  const indicatorColorGradientDirection = settings[ComponentSettingKey.LinearProgressDesignCircleColorGradientDirection]

  const trackColorType = settings[ComponentSettingKey.LinearProgressDesignTrackColor]
  const trackColorSolid = settings[ComponentSettingKey.LinearProgressDesignTrackColorSolid] || 'primary-40'
  const trackColorStartGradient =
    settings[ComponentSettingKey.LinearProgressDesignTrackColorStartGradient] || 'primary-40'
  const trackColorEndGradient = settings[ComponentSettingKey.LinearProgressDesignTrackColorEndGradient] || 'primary-40'
  const trackColorGradientDirection = settings[ComponentSettingKey.LinearProgressDesignTrackColorGradientDirection]

  const compSettings: CompLinearProgressProps = {
    value: Number(currentValue),
    max: Number(maxValue),
    width,
    alignment: compAlignment as ComponentAlignment,
    height: `${height}px`,
    margin,
    valueTypography,
    valueSpacing,
    backgroundColor: currentParentBg || 'primary-0',
    valueColorType,
    valueColorSolid,
    valueColorStartGradient,
    valueColorEndGradient,
    valueColorGradientDirection,
    indicatorColorType,
    indicatorColorSolid,
    indicatorColorStartGradient,
    indicatorColorEndGradient,
    indicatorColorGradientDirection,
    trackColorType,
    trackColorSolid,
    trackColorStartGradient,
    trackColorEndGradient,
    trackColorGradientDirection,
    titleComponent: (
      <CompHeadline
        containerClient={containerClient}
        detailTableId={detailTableId}
        detailRowIndex={detailRowIndex}
        headline={heading as unknown as Descendant[]}
        compTitleAlign={headingAlignment}
        headlineMargin={headingMargin}
        headlineTypo={headingTypography as TypographySelectValue}
        backgroundColor={currentParentBg}
        colorType={labelColorType}
        styles={getTextColorStyles(
          labelColorType,
          labelColorSolid,
          labelColorStartGradient,
          labelColorEndGradient,
          labelColorGradientDirection,
          currentParentBg || 'primary-90'
        )}
      />
    ),
    valueAlignment: 'end' as TextAlignment,
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const linearProgressComponent = children(compSettings)

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {linearProgressComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return linearProgressComponent
}
